import type {
  FillGradient,
  GradientColorStop,
  GradientFillMode,
} from '~/design-system/components/DeckGen/types/presentation'
import { gradientCssToStops, toRgba } from '~/design-system/components/ColorSelector/gradientCss'

/**
 * Bridge between the model's structured {@link FillGradient} and the CSS `background` string used for
 * render. The structured field is the editable source of truth; this serializes it to CSS (and parses a
 * baked CSS string back) using the SAME helpers the color picker uses, so the editor swatch, the rendered
 * fill, and the stored string never disagree.
 */

/** Default linear axis angle in CSS degrees (90 = left→right, i.e. the old `to right`). */
export const DEFAULT_LINEAR_ANGLE = 90

const norm360 = (deg: number) => ((deg % 360) + 360) % 360
const round2 = (n: number) => Math.round(n * 100) / 100
const sortedStopsCss = (stops: FillGradient['stops']) =>
  [...stops].sort((a, b) => a.position - b.position).map((s) => ({ s, css: toRgba(s.color, s.opacity ?? 100) }))

/**
 * A LINEAR gradient defined by two box-relative endpoints → a CSS `linear-gradient(angle, …)`. CSS has no
 * arbitrary endpoints, so we derive the axis ANGLE from the two points (in px, honouring the box aspect)
 * and REMAP each stop's position onto the box-fitting gradient line so the colors land exactly where the
 * endpoints are. Requires the box size; without it, callers fall back to {@link angleFromPoints}.
 */
function linearFromPoints(gradient: FillGradient, width: number, height: number): string {
  const s = gradient.start!
  const e = gradient.end!
  const S = { x: (s.x / 100) * width, y: (s.y / 100) * height }
  const E = { x: (e.x / 100) * width, y: (e.y / 100) * height }
  const theta = norm360((Math.atan2(E.x - S.x, -(E.y - S.y)) * 180) / Math.PI)
  const rad = (theta * Math.PI) / 180
  const ax = Math.sin(rad)
  const ay = -Math.cos(rad)
  const halfLen = (Math.abs(width * Math.sin(rad)) + Math.abs(height * Math.cos(rad))) / 2 || 1
  const cx = width / 2
  const cy = height / 2
  const frac = (p: { x: number; y: number }) => 0.5 + ((p.x - cx) * ax + (p.y - cy) * ay) / (2 * halfLen)
  const fS = frac(S)
  const fE = frac(E)
  const colorStops = sortedStopsCss(gradient.stops)
    .map(({ s: stop, css }) => `${css} ${round2((fS + (stop.position / 100) * (fE - fS)) * 100)}%`)
    .join(', ')
  return `linear-gradient(${round2(theta)}deg, ${colorStops})`
}

/** Approximate axis angle from box-relative endpoints WITHOUT the box (aspect ignored) — the graceful
 *  fallback when a consumer serializes a start/end gradient but can't supply the box size. */
function angleFromPoints(gradient: FillGradient): number {
  return norm360((Math.atan2(gradient.end!.x - gradient.start!.x, -(gradient.end!.y - gradient.start!.y)) * 180) / Math.PI)
}

/** Serialize a structured gradient fill to its CSS `background` string — honouring linear endpoints
 *  (`start`/`end`, box-aware), the axis `angle` (linear/conic), `position` (radial/conic centre) and
 *  radial `radius` so the on-canvas handles round-trip. Pass `box` for an exact start/end linear axis. */
export function gradientToCss(gradient: FillGradient, box?: { width: number; height: number }): string {
  const colorStops = sortedStopsCss(gradient.stops)
    .map(({ s, css }) => `${css} ${s.position}%`)
    .join(', ')
  if (gradient.mode === 'radial') {
    const size = gradient.radius != null ? `circle ${Math.round(gradient.radius)}px` : 'circle'
    const at = gradient.position ? ` at ${gradient.position.x}% ${gradient.position.y}%` : ''
    return `radial-gradient(${size}${at}, ${colorStops})`
  }
  if (gradient.mode === 'angular') {
    const from = gradient.angle != null ? `from ${gradient.angle}deg` : ''
    const at = gradient.position ? `at ${gradient.position.x}% ${gradient.position.y}%` : ''
    const prefix = [from, at].filter(Boolean).join(' ')
    return `conic-gradient(${prefix ? `${prefix}, ` : ''}${colorStops})`
  }
  if (gradient.start && gradient.end) {
    return box ? linearFromPoints(gradient, box.width, box.height) : `linear-gradient(${round2(angleFromPoints(gradient))}deg, ${colorStops})`
  }
  return `linear-gradient(${gradient.angle ?? DEFAULT_LINEAR_ANGLE}deg, ${colorStops})`
}

/** Best-effort parse of a CSS gradient `background` string into an editable {@link FillGradient}, or null
 *  when it isn't a recognizable gradient (the caller keeps the raw string for render and edits as solid). */
export function cssToGradient(background: string | undefined): FillGradient | null {
  // A MULTI-LAYER fill — a gradient with a co-present `url()`/pattern overlay, or two stacked gradients —
  // is NOT a single editable gradient. Parsing one would silently drop the other layer(s), and the first
  // gradient edit would then re-serialize `background` from the lone gradient and DESTROY the pattern
  // permanently (the "only the gradient survived" bake bug). Keep the raw multi-layer string for render
  // and treat the fill as a frozen string instead, so every layer is preserved.
  // A `repeating-*-gradient` (a line / stripe / grid pattern) can't be expressed as a stop list — parsing
  // it would flatten the sharp repeated bands into ONE smooth gradient. Keep the raw string for render.
  if (
    background &&
    (background.includes('url(') ||
      background.includes('repeating-') ||
      (background.match(/gradient\(/g)?.length ?? 0) > 1)
  ) {
    return null
  }
  const parsed = gradientCssToStops(background)
  if (!parsed || parsed.mode === 'solid') return null
  return {
    mode: parsed.mode as GradientFillMode,
    stops: parsed.stops as GradientColorStop[],
    angle: parsed.angle,
    position: parsed.position,
    radius: parsed.radius,
  }
}

/** The gradient fill's overall opacity (0–100) for a single opacity control — the most-opaque stop. */
export function gradientOverallOpacity(gradient: FillGradient): number {
  return gradient.stops.reduce((max, s) => Math.max(max, s.opacity ?? 100), 0)
}

/** Scale every stop's opacity so the gradient's overall opacity becomes `op` (0–100), preserving the
 *  relative alpha between stops. When the current overall is 0, all stops are set to `op` directly. */
export function withGradientOpacity(gradient: FillGradient, op: number): FillGradient {
  const target = Math.max(0, Math.min(100, Math.round(op)))
  const current = gradientOverallOpacity(gradient)
  const stops = gradient.stops.map((s) => {
    const next = current > 0 ? Math.round((s.opacity ?? 100) * (target / current)) : target
    return { ...s, opacity: Math.max(0, Math.min(100, next)) }
  })
  return { ...gradient, stops }
}

let seedStopId = 0

/** Seed a 2-stop gradient from a solid color (the current fill) → the color fading to transparent. Used
 *  when switching a solid fill into gradient mode so the user starts from their existing color. */
export function seedGradientStops(hex: string): GradientColorStop[] {
  return [
    { id: `seed-${++seedStopId}`, position: 0, color: hex, opacity: 100 },
    { id: `seed-${++seedStopId}`, position: 100, color: hex, opacity: 0 },
  ]
}
